import { useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  loading: boolean,
  result: T | undefined,
  error: Error | undefined,
}

const InitialAsyncState = {
  loading: true,
  result: undefined,
  error: undefined,
};

const defaultSetLoading = <T>(asyncState: AsyncState<T>) => InitialAsyncState;
const defaultSetResult = <T>(result: T, asyncState: AsyncState<T>) => ({
  loading: false,
  result: result,
  error: undefined,
});
const defaultSetError = <T>(error: Error, asyncState: AsyncState<T>) => ({
  loading: false,
  result: undefined,
  error: error,
});

const DefaultOptions = {
  setLoading: defaultSetLoading,
  setResult: defaultSetResult,
  setError: defaultSetError,
};

const normalizeOptions = (options: any) => ({
  ...DefaultOptions,
  ...options,
});

const useAsyncState = <T>(options: any) => {
  const [value, setValue] = useState(InitialAsyncState);
  return {
    value,
    setLoading: () => setValue(options.setLoading(value)),
    setResult: (result: T)  => setValue(options.setResult(result, value)),
    setError: (error: Error) => setValue(options.setError(error, value)),
  }
};

const useIsMounted = () => {
  const ref = useRef(false);
  useEffect(() => {
    ref.current = true;
    return () => { ref.current = false };
  }, []);
  return () => ref.current;
};

const useCurrentPromise = () => {
  const ref = useRef<any>(null);
  return {
    set: ((promise: Promise<any>) => ref.current = promise),
    is: ((promise: Promise<any>) => ref.current === promise),
  };
};

const useAsync = <T>(
  asyncFunction: () => Promise<T>,
  params = [],
  options?: any
) => {

  options = normalizeOptions(options);

  const AsyncState = useAsyncState(options);

  const isMounted = useIsMounted();

  const CurrentPromise = useCurrentPromise();

  // We only want to handle the promise result/error
  // if it is the last operation and the comp is still mounted
  const shouldHandlePromise = (p: Promise<T>) => isMounted() && CurrentPromise.is(p);

  const executeAsyncOperation = () => {
    const promise = asyncFunction();
    CurrentPromise.set(promise);
    AsyncState.setLoading();
    promise.then(
      (result: T) => {
        if ( shouldHandlePromise(promise) ) {
          AsyncState.setResult(result);
        }
      },
      (error: Error) => {
        // REVIEW FOR DEV
        // message.error(`请求失败: ${error.message}`);
        if ( shouldHandlePromise(promise) ) {
          AsyncState.setError(error);
        }
      }
    );
  };

  useEffect(() => {
    executeAsyncOperation();
  }, params);


  const result = {
    ...AsyncState.value,
    setResult: AsyncState.setResult,
    execute: () => executeAsyncOperation(),
  };

  return result;
};

export default useAsync;